<template>
  <div class="content-box-dialog">
    <div class="zoom-actions">
      <v-btn @click="zoomIn">+</v-btn>
      <v-btn class="ml-2" @click="zoomOut">-</v-btn>
      <v-btn class="ml-2" @click="reset">
        <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
      </v-btn>
      <v-btn class="ml-2" :color="rearranging ? 'primary' : ''" @click="toggleRearrange">
        <v-icon class="mr-1" small>mdi-arrow-all</v-icon>
        {{ $t("custom.content_box.rearrange") }}
      </v-btn>
    </div>

    <v-form ref="form" v-model="isFormValid" class="dialog-body">
      <div class="panzoom-parent">
        <div ref="panzoom" class="panzoom-content">
          <div
            class="rows-preview"
            :class="{ 'rows-preview--rearranging': rearranging }"
            :style="{ '--max-columns': maxColumns }">
            <draggable
              v-model="formData.rows"
              :disabled="!rearranging"
              filter=".column-wrapper, .add-tile, .v-btn"
              :prevent-on-filter="false"
              group="content-box-rows"
              :force-fallback="true"
              ghost-class="row-wrapper--ghost"
              :animation="150"
              @end="onDragEnd">
              <div
                v-for="(row, rowIndex) in formData.rows"
                :key="row.uid"
                class="row-wrapper"
                :class="{ 'panzoom-ignore': rearranging }">
                <div v-if="rearranging" class="row-handle panzoom-ignore">
                  <v-icon size="24" color="white">mdi-drag-vertical</v-icon>
                </div>

                <div class="row-main">
                  <div class="row-header">
                    <span class="row-title">{{ $t("custom.content_box.row", { index: rowIndex + 1 }) }}</span>
                    <v-btn v-if="formData.rows.length > 1" icon small @click="removeRow(rowIndex)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>

                  <div class="row-body">
                    <draggable
                      v-model="row.columns"
                      class="columns"
                      :disabled="!rearranging"
                      group="content-box-columns"
                      :force-fallback="true"
                      ghost-class="column-wrapper--ghost"
                      :animation="150"
                      @end="onDragEnd">
                      <div
                        v-for="(column, columnIndex) in row.columns"
                        :key="column.uid"
                        class="column-wrapper panzoom-ignore">
                        <div class="column-header">
                          <v-icon v-if="rearranging" class="column-handle" size="24">mdi-drag-vertical</v-icon>
                          <span class="column-title">{{
                            $t("custom.content_box.column", { index: columnIndex + 1 })
                          }}</span>
                          <span class="column-width-label">{{ $t("custom.content_box.page_width") }}</span>
                          <v-text-field
                            v-model.number="column.width"
                            class="column-width"
                            type="number"
                            min="1"
                            max="100"
                            suffix="%"
                            single-line
                            hide-details="auto"
                            dense
                            :rules="[v => !v || (v > 0 && v <= 100) || $t('custom.content_box.width_range')]" />
                          <v-btn v-if="row.columns.length > 1" icon small @click="removeColumn(row, columnIndex)">
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                        <VaTinymceInput
                          ref="editors"
                          :key="editorKey"
                          :stripped-down="true"
                          :disable-resize="true"
                          :editor-height="350"
                          :current-content="column.content"
                          @update="val => (column.content = val)" />
                        <div v-if="rearranging" class="column-overlay" />
                      </div>
                    </draggable>

                    <button type="button" class="add-tile add-tile--column" @click="addColumn(row)">
                      <v-icon size="16" color="primary">mdi-plus</v-icon>
                      {{ $t("custom.content_box.add_column") }}
                    </button>
                  </div>
                </div>
              </div>
            </draggable>

            <button type="button" class="add-tile mb-4" @click="addRow">
              <v-icon size="16" color="primary" class="mr-1">mdi-plus</v-icon>
              {{ $t("custom.content_box.add_row") }}
            </button>
          </div>
        </div>
      </div>

      <aside class="settings-sidebar">
        <div class="control-group">
          <div class="spacing-row">
            <div class="border-panel">
              <span class="group-title">{{ $t("custom.content_box.borders") }}</span>
              <VaBorderInput
                :border-style.sync="formData.borderStyle"
                :border-width.sync="formData.borderWidth"
                :border-color.sync="formData.borderColor"
                :length.sync="formData.lineLength"
                :linked.sync="borderLinked" />
            </div>

            <div class="spacing-column">
              <span class="group-title">{{ $t("custom.content_box.spacing") }}</span>
              <VaSpacingInput
                :margin.sync="formData.margin"
                :padding.sync="formData.padding"
                :margin-linked.sync="marginLinked"
                :padding-linked.sync="paddingLinked"
                :side-margins-disabled="formData.fullWidth" />
            </div>
          </div>
        </div>

        <div class="control-group">
          <span class="group-title">{{ $t("custom.content_box.box") }}</span>
          <v-switch
            v-model="formData.enableBackground"
            :label="$t('custom.content_box.background')"
            class="mt-0"
            hide-details
            dense />
          <VaColorPicker
            v-if="formData.enableBackground"
            :position="'top'"
            :title="$t('custom.content_box.background_color')"
            v-model="formData.backgroundColor" />
          <v-switch
            v-model="formData.fullWidth"
            :label="$t('custom.content_box.full_width')"
            class="mt-0"
            hide-details
            dense />
          <v-switch
            v-model="formData.enableMaxWidth"
            :label="$t('custom.content_box.custom_max_width')"
            class="mt-0"
            hide-details
            dense />
          <v-text-field
            v-if="formData.enableMaxWidth"
            v-model.number="formData.maxWidth"
            :label="$t('custom.content_box.max_width')"
            type="number"
            min="200"
            suffix="px"
            hide-details="auto"
            :rules="[v => v >= 200 || $t('custom.content_box.min_max_width')]" />
          <v-text-field
            v-model.number="formData.cornerRadius"
            :label="$t('custom.content_box.corner_radius')"
            type="number"
            min="0"
            suffix="px"
            hide-details="auto"
            :rules="[v => v >= 0 || $t('custom.content_box.not_negative')]" />
        </div>

        <v-switch
          v-model="formData.separateMobile"
          class="separate-mobile"
          :label="$t('custom.content_box.separate_mobile')"
          hide-details
          dense />

        <div v-if="formData.separateMobile" class="mobile-overrides">
          <span class="group-title mobile-title">{{ $t("custom.content_box.mobile_title") }}</span>
          <div class="control-group">
            <div class="spacing-row">
              <div class="border-panel">
                <span class="group-title">{{ $t("custom.content_box.borders") }}</span>
                <VaBorderInput
                  :border-style.sync="formData.mobile.borderStyle"
                  :border-width.sync="formData.mobile.borderWidth"
                  :border-color.sync="formData.mobile.borderColor"
                  :length.sync="formData.mobile.lineLength"
                  :linked.sync="mobileBorderLinked" />
              </div>

              <div class="spacing-column">
                <span class="group-title">{{ $t("custom.content_box.spacing") }}</span>
                <VaSpacingInput
                  :margin.sync="formData.mobile.margin"
                  :padding.sync="formData.mobile.padding"
                  :margin-linked.sync="mobileMarginLinked"
                  :padding-linked.sync="mobilePaddingLinked"
                  :side-margins-disabled="formData.mobile.fullWidth" />
              </div>
            </div>
          </div>

          <div class="control-group">
            <span class="group-title">{{ $t("custom.content_box.box") }}</span>
            <v-switch
              v-model="formData.mobile.enableBackground"
              :label="$t('custom.content_box.background')"
              class="mt-0"
              hide-details
              dense />
            <VaColorPicker
              v-if="formData.mobile.enableBackground"
              :position="'top'"
              :title="$t('custom.content_box.background_color')"
              v-model="formData.mobile.backgroundColor" />
            <v-switch
              v-model="formData.mobile.fullWidth"
              :label="$t('custom.content_box.full_width')"
              class="mt-0"
              hide-details
              dense />
            <v-text-field
              v-model.number="formData.mobile.cornerRadius"
              :label="$t('custom.content_box.corner_radius')"
              type="number"
              min="0"
              suffix="px"
              hide-details="auto"
              :rules="[v => v >= 0 || $t('custom.content_box.not_negative')]" />
          </div>
        </div>

        <v-alert v-if="errorMessage" type="error" class="mb-4" dismissible @input="errorMessage = ''">
          {{ errorMessage }}
        </v-alert>
      </aside>
    </v-form>
  </div>
</template>

<script>
  import cloneDeep from "lodash/cloneDeep";
  import {
    CONTENT_BOX_EVENT_TYPES,
    DEFAULT_CONTENT_BOX_MARGIN_BOX,
    DEFAULT_CONTENT_BOX_PADDING,
    DEFAULT_CONTENT_BOX_LINE_LENGTH,
    DEFAULT_CONTENT_BOX_BORDER_WIDTH,
    DEFAULT_CONTENT_BOX_BORDER_COLOR,
  } from "@/constant";
  import VaBorderInput from "@/components/custom/VaBorderInput.vue";
  import VaSpacingInput from "@/components/custom/VaSpacingInput.vue";
  import VaColorPicker from "@/components/custom/VaColorPicker.vue";
  import VaTinymceInput from "@/components/custom/VaTinymceInput.vue";
  import Panzoom from "@panzoom/panzoom";
  import draggable from "vuedraggable";

  const DEFAULT_COLUMN_CONTENT =
    "<h3>It Only Takes 60 Seconds To Check If You Qualify!</h3><p>Thousands of seniors have already checked their eligibility. Don't leave money you're entitled to unclaimed.</p>";

  const SIDES = [
    { key: "top", label: "Top" },
    { key: "right", label: "Right" },
    { key: "bottom", label: "Bottom" },
    { key: "left", label: "Left" },
  ];

  const DEFAULT_FORM_DATA = {
    borderStyle: "solid",
    borderColor: DEFAULT_CONTENT_BOX_BORDER_COLOR,
    borderWidth: { top: DEFAULT_CONTENT_BOX_BORDER_WIDTH, right: 0, bottom: 0, left: 0 },
    cornerRadius: 0,
    enableBackground: true,
    backgroundColor: "#EEF2FB",
    fullWidth: false,
    margin: { ...DEFAULT_CONTENT_BOX_MARGIN_BOX },
    padding: { ...DEFAULT_CONTENT_BOX_PADDING },
    lineLength: DEFAULT_CONTENT_BOX_LINE_LENGTH,
    separateMobile: false,
    mobile: {
      fullWidth: false,
      borderStyle: "solid",
      borderColor: DEFAULT_CONTENT_BOX_BORDER_COLOR,
      borderWidth: { top: DEFAULT_CONTENT_BOX_BORDER_WIDTH, right: 0, bottom: 0, left: 0 },
      enableBackground: true,
      margin: { ...DEFAULT_CONTENT_BOX_MARGIN_BOX },
      padding: { ...DEFAULT_CONTENT_BOX_PADDING },
      lineLength: DEFAULT_CONTENT_BOX_LINE_LENGTH,
      cornerRadius: 0,
      backgroundColor: "#EEF2FB",
    },
    enableMaxWidth: false,
    maxWidth: 770,
    rows: [],
  };

  const isUniform = (box = {}) => SIDES.every(side => box[side.key] === box.top);

  const isNonEmptyHtml = html => {
    if (!html) return false;
    return (
      html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim().length > 0
    );
  };

  let contentBoxUid = 0;

  export default {
    name: "ContentBoxDialog",
    components: {
      VaBorderInput,
      VaSpacingInput,
      VaColorPicker,
      VaTinymceInput,
      draggable,
    },
    data() {
      return {
        panzoomInstance: null,
        rearranging: false,
        editorKey: 0,
        marginLinked: isUniform(DEFAULT_FORM_DATA.margin),
        mobileMarginLinked: isUniform(DEFAULT_FORM_DATA.mobile.margin),
        paddingLinked: isUniform(DEFAULT_FORM_DATA.padding),
        mobilePaddingLinked: isUniform(DEFAULT_FORM_DATA.mobile.padding),
        borderLinked: isUniform(DEFAULT_FORM_DATA.borderWidth),
        mobileBorderLinked: isUniform(DEFAULT_FORM_DATA.mobile.borderWidth),
        isFormValid: false,
        errorMessage: "",
        mobileCustomized: false,
        formData: {
          ...cloneDeep(DEFAULT_FORM_DATA),
          rows: [this.createRow([this.createColumn(DEFAULT_COLUMN_CONTENT)])],
        },
      };
    },
    computed: {
      maxColumns() {
        return String(Math.max(1, ...this.formData.rows.map(row => row.columns.length)));
      },
      isAllValid() {
        return (
          this.isFormValid && this.formData.rows.some(row => row.columns.some(column => isNonEmptyHtml(column.content)))
        );
      },
    },
    watch: {
      isAllValid: {
        handler() {
          this.sendValidation();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
      // Starts as a copy of desktop, so switching it on changes nothing by itself.
      "formData.separateMobile"(enabled) {
        if (!enabled || this.mobileCustomized) return;

        this.mobileCustomized = true;
        this.formData.mobile = {
          fullWidth: this.formData.fullWidth,
          borderStyle: this.formData.borderStyle,
          borderWidth: { ...this.formData.borderWidth },
          borderColor: this.formData.borderColor,
          enableBackground: this.formData.enableBackground,
          margin: { ...this.formData.margin },
          padding: { ...this.formData.padding },
          lineLength: this.formData.lineLength,
          cornerRadius: this.formData.cornerRadius,
          backgroundColor: this.formData.backgroundColor,
        };
        this.mobileMarginLinked = this.marginLinked;
        this.mobilePaddingLinked = this.paddingLinked;
        this.mobileBorderLinked = this.borderLinked;
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.panzoomInstance = Panzoom(this.$refs.panzoom, {
        canvas: true,
        excludeClass: "panzoom-ignore",
        handleStartEvent: () => {},
      });

      this.$refs.panzoom.parentElement.addEventListener("wheel", event => {
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      });
    },
    methods: {
      reset() {
        this.panzoomInstance.reset();
      },
      zoomIn() {
        this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        this.panzoomInstance.zoomOut();
      },
      toggleRearrange() {
        (this.$refs.editors || []).forEach(editor => editor.flushContent());
        this.rearranging = !this.rearranging;
      },
      onDragEnd(event) {
        if (event.from === event.to && event.oldIndex === event.newIndex) return;

        this.formData.rows = this.formData.rows.filter(row => row.columns.length);
        this.editorKey += 1;
      },
      createColumn(content = "", width = null) {
        return { uid: ++contentBoxUid, content, width };
      },
      createRow(columns) {
        return { uid: ++contentBoxUid, columns: columns || [this.createColumn()] };
      },
      addRow() {
        this.formData.rows.push(this.createRow());
      },
      removeRow(index) {
        this.formData.rows.splice(index, 1);
      },
      addColumn(row) {
        row.columns.push(this.createColumn());
      },
      removeColumn(row, index) {
        row.columns.splice(index, 1);
      },
      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (message.data.type === CONTENT_BOX_EVENT_TYPES.NEED_VALIDATION) {
            await this.$refs.form.validate();
            if (this.isAllValid) {
              this.updateValue();
              this.sendValidation();
              this.errorMessage = "";
            } else {
              this.errorMessage = this.$t("custom.content_box.empty_content");
            }
          }
          if (message.data.type === CONTENT_BOX_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data && Object.keys(data).length > 0) {
              this.mobileCustomized = Boolean(data.separateMobile);
              this.formData = {
                ...cloneDeep(DEFAULT_FORM_DATA),
                ...data,
                mobile: { ...cloneDeep(DEFAULT_FORM_DATA.mobile), ...data.mobile },
                rows: (data.rows || []).map(row =>
                  this.createRow((row.columns || []).map(column => this.createColumn(column.content, column.width))),
                ),
              };
              this.marginLinked = isUniform(this.formData.margin);
              this.mobileMarginLinked = isUniform(this.formData.mobile.margin);
              this.paddingLinked = isUniform(this.formData.padding);
              this.mobilePaddingLinked = isUniform(this.formData.mobile.padding);
              this.borderLinked = isUniform(this.formData.borderWidth);
              this.mobileBorderLinked = isUniform(this.formData.mobile.borderWidth);
            } else {
              this.updateValue();
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: CONTENT_BOX_EVENT_TYPES.REQUEST_DATA,
          },
          "*",
        );
      },
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isAllValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      updateValue() {
        const data = cloneDeep(this.formData);
        data.rows = data.rows.map(row => ({
          columns: row.columns.map(column => ({ content: column.content, width: column.width ?? null })),
        }));
        window.parent.postMessage(
          {
            mceAction: CONTENT_BOX_EVENT_TYPES.UPDATE_CONTENT_BOX_DATA,
            data,
          },
          "*",
        );
      },
      sendValidation() {
        window.parent.postMessage(
          {
            mceAction: CONTENT_BOX_EVENT_TYPES.VALIDATION,
            data: {
              isValid: this.isAllValid,
            },
          },
          "*",
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";

  $column-width: 640px;
  $column-gap: 12px;

  .sortable-fallback {
    display: none !important;
  }

  .zoom-actions {
    position: fixed;
    left: 20px;
    bottom: 20px;
    z-index: 100;
  }

  .content-box-dialog {
    .dialog-body {
      display: flex;
      align-items: stretch;
      height: 100vh;
    }

    .panzoom-parent {
      flex: 1;
      min-width: 0;
      background: get-color("element-bg-300");
    }

    .panzoom-content {
      height: 100%;
    }

    .settings-sidebar {
      flex: 0 0 400px;
      overflow-y: auto;
      padding: 20px;
      background: get-color("white");
      border-top: 1px solid get-color("border");
      border-left: 1px solid get-color("border");
    }

    .rows-preview {
      width: max-content;
      margin: 0 auto;
      padding: 20px;
      background: get-color("white");
    }

    .row-wrapper {
      display: flex;
      align-items: stretch;
      border: 1px solid get-color("border");
      border-radius: 4px;
      margin-bottom: 16px;
      overflow: hidden;
    }

    .row-main {
      flex: 1 1 auto;
      min-width: 0;
      padding: 12px;
    }

    .row-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;

      .row-title {
        font-size: 14px;
        font-weight: 700;
      }
    }

    .row-handle {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 32px;
      background-color: get-color("primary");
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }

    .column-handle {
      margin-right: 4px;
      cursor: grab;
      color: get-color("primary") !important;

      &:active {
        cursor: grabbing;
      }
    }

    .row-wrapper--ghost,
    .column-wrapper--ghost {
      opacity: 0.4;
    }

    .rows-preview--rearranging {
      .row-wrapper,
      .column-wrapper {
        cursor: grab;

        &:active {
          cursor: grabbing;
        }
      }

      .row-wrapper {
        border: 1.5px dashed get-color("primary-65");
      }

      .column-wrapper {
        outline: 1.5px dashed get-color("primary-65");
        outline-offset: 2px;
      }
    }

    .row-body {
      display: flex;
      align-items: stretch;
      gap: 12px;
    }

    .columns {
      display: flex;
      flex: 0 0 auto;
      gap: $column-gap;
      width: calc(var(--max-columns) * #{$column-width} + (var(--max-columns) - 1) * #{$column-gap});
    }

    .column-wrapper {
      position: relative;
      flex: 1 1 0;
      min-width: 0;
    }

    .column-overlay {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 2;
      border-radius: 4px;
      background-color: get-color("primary");
      opacity: 0.18;
    }

    .add-tile {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 100%;
      padding: 8px;
      border: 1px dashed get-color("primary-65");
      border-radius: 6px;
      background-color: get-color("primary-10-hover");
      color: get-color("primary");
      cursor: pointer;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &--column {
        flex: 0 0 132px;
        flex-direction: column;
        width: auto;
      }
    }

    .group-title {
      display: block;
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 4px;
    }

    .control-group {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding-bottom: 10px;

      & + .control-group {
        border-top: 1px solid get-color("border");
        padding-top: 10px;
      }
    }

    .spacing-row {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .spacing-column,
    .border-panel {
      min-width: 0;
    }

    .separate-mobile {
      margin: 6px 0 2px;
    }

    .mobile-title {
      margin-bottom: 8px;
    }

    .mobile-overrides {
      padding-top: 12px;
      margin-bottom: 16px;
    }

    .column-width-label {
      margin-left: auto;
      margin-right: 8px;
      font-size: 13px;
    }

    .column-width {
      flex: 0 0 96px;
      margin: 0 8px 0 0;
      padding-top: 0;

      ::v-deep input {
        padding-left: 8px;
      }
    }

    .column-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 4px;

      .column-title {
        font-size: 13px;
        font-weight: 600;
      }
    }

    ::v-deep .v-text-field__suffix {
      padding-right: 6px;
    }
  }
</style>
